<template>
  <div
    ref="wrapper"
    class="d-flex flex-column justify-content-between"
    style="min-height:0px;"
  >
    <div
      v-if="empty_percentage !== undefined"
      style="height:4px; margin-top:2px; margin-bottom:2px; "
    >
      <canvas ref="empty-percentage-chart" height="4" :width="cell_width" />
    </div>

    <div
      class="d-flex flex-row justify-content-around"
      style="height:56px; margin-top:10px;"
    >
      <canvas ref="hits-chart" height="56" />
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import $ from 'jquery'
import Chart from 'chart.js'

import chartColors from '@/components/chart_colors.js'
import { display_format_element } from '@/components/columnstore_viewer_common.js'

// *****************************************************************************

function parameterizeTooltip(category, tooltipModel) {
  // Tooltip Element
  let tooltipEl = document.getElementById('chartjs-tooltip')

  // Create element on first render
  if (!tooltipEl) {
    tooltipEl = document.createElement('div')
    tooltipEl.id = 'chartjs-tooltip'
    tooltipEl.innerHTML = '<table></table>'
    document.body.appendChild(tooltipEl)
  }

  // Hide if no tooltip
  if (tooltipModel.opacity === 0) {
    tooltipEl.style.opacity = 0
    return
  }

  // Set caret Position
  tooltipEl.classList.remove('above', 'below', 'no-transform')
  if (tooltipModel.yAlign) {
    tooltipEl.classList.add(tooltipModel.yAlign)
  } else {
    tooltipEl.classList.add('no-transform')
  }

  function getBody(bodyItem) {
    return bodyItem.lines
  }
  // Set Text
  if (tooltipModel.body) {
    let titleLines = tooltipModel.title || []
    let bodyLines = tooltipModel.body.map(getBody)

    let innerHtml = '<thead>'

    titleLines.forEach(function(title) {
      innerHtml += '<tr><th>' + title + '</th></tr>'
    })
    innerHtml += '</thead><tbody>'

    bodyLines.forEach(function(body, i) {
      let colors = tooltipModel.labelColors[i]
      let style = 'background:' + colors.backgroundColor
      style += '; border-color:' + colors.borderColor
      style += '; border-width: 2px'
      let span = '<span style="' + style + '"></span>'
      innerHtml += '<tr><td>' + span + body[0] + '%' + '</td></tr>'
      if (tooltipModel.title[0] !== 'Values filling') {
        innerHtml += '<tr><td>' + span + body[1] + '</td></tr>'
      }
    })
    innerHtml += '</tbody>'

    let tableRoot = tooltipEl.querySelector('table')
    tableRoot.innerHTML = innerHtml
  }

  // `this` will be the overall tooltip
  let position = this._chart.canvas.getBoundingClientRect()

  // Display, position, and set styles for font
  tooltipEl.style.opacity = 0.7
  tooltipEl.style.backgroundColor = 'black'
  tooltipEl.style.position = 'absolute'
  tooltipEl.style.color = 'white'
  tooltipEl.style.borderRadius = '10px'
  if (category === 0) {
    tooltipEl.style.left =
      position.left + window.pageXOffset + tooltipModel.caretX + 5 + 'px'
    tooltipEl.style.top = position.top + window.pageYOffset + 5 + 'px'
  } else {
    tooltipEl.style.left = position.left + window.pageXOffset + 5 + 'px'
    tooltipEl.style.top =
      position.top + window.pageYOffset + tooltipModel.caretY + 5 + 'px'
  }
  tooltipEl.style.fontFamily = tooltipModel._bodyFontFamily
  tooltipEl.style.fontSize = tooltipModel.bodyFontSize + 'px'
  tooltipEl.style.fontStyle = tooltipModel._bodyFontStyle
  tooltipEl.style.padding =
    tooltipModel.yPadding + 'px ' + tooltipModel.xPadding + 'px'
  tooltipEl.style.pointerEvents = 'none'
  tooltipEl.style.overflow = 'visible'
}

function cleanup_parameterizeTooltip() {
  let tooltipEl = document.getElementById('chartjs-tooltip')
  if (tooltipEl) {
    tooltipEl.parentNode.removeChild(tooltipEl)
  }
}

function format_generic_label(tooltipItem, data) {
  let label = data.datasets[0].data[tooltipItem.index].toFixed(1)
  let nb_obs = data.nb_obs_per_category[tooltipItem.index]
  return [label, nb_obs]
}

class EmptyPercentageChart {
  constructor(el, empty_percentage) {
    this.el = el
    let empty = Math.round(empty_percentage * 100)
    this.values = [100 - empty, empty]

    this.real_chart = new Chart(this.el, {
      type: 'horizontalBar',
      data: {
        labels: ['Values filling'],
        datasets: [
          {
            label: 'Filled',
            stack: 'Stack0',
            data: [this.values[0]],
            backgroundColor: chartColors['green'],
            barThickness: 4,
          },
          {
            label: 'Empty',
            stack: 'Stack0',
            data: [this.values[1]],
            backgroundColor: chartColors['red'],
            barThickness: 4,
          },
        ],
      },
      options: {
        responsive: false,

        legend: {
          display: false,
        },

        tooltips: {
          position: 'nearest',
          enabled: false,
          custom: function(ttm) {
            return parameterizeTooltip.bind(this)(1, ttm)
          },
        },

        scales: {
          xAxes: [
            {
              display: false,
            },
          ],
          yAxes: [
            {
              display: false,
              ticks: {
                beginAtZero: true,
                max: Math.max(...this.values),
              },
            },
          ],
        },
      },
    })
  }
}

class GenericChart {
  constructor(el, histogram, bar_width, nb_bars, nb_obs) {
    this.el = el
    this.bar_width = bar_width
    this.nb_bars = nb_bars
    this.nb_obs = nb_obs
    this.nb_obs_per_category = histogram.histogram.slice(0, nb_bars)
    this.true_values = _.map(this.nb_obs_per_category, function(x) {
      return (x / nb_obs) * 100
    })
    this.labels = histogram.precomputed_histogram_metadata[0].slice(0, nb_bars)

    this.labels = _.map(this.labels, x => {
      // if (_.isString(x) || _.isNumber(x) ) {
      if (_.isString(x) || _.isNumber(x) || _.isBoolean(x)) {
        // In case of a single value, we just return the value itself
        // (there's only on distinct value in the whole column)
        return x
      } else {
        // Otherwise we have some ranges from which we derived
        // the best formatting which we apply
        let res = _.map(x, y =>
          display_format_element(
            y,
            histogram.category,
            histogram.header_display_format
          )
        )
        return res[0] + ' &hellip; ' + res[1]
      }
      // this.labels returns value if string or number, else it returns bins ranges
    })

    this.max_value = Math.max(...this.true_values)
    this.completion_values = this.true_values.map(x => this.max_value - x)
    let datasets = [
      {
        data: this.true_values,
        backgroundColor: chartColors['lightblue'],
        stack: 'Stack1',
        barThickness: this.bar_width - 1,
      },
      {
        data: this.completion_values,
        backgroundColor: chartColors['white'],
        stack: 'Stack1',
        barThickness: this.bar_width - 1,
      },
    ]

    this.real_chart = new Chart(this.el, {
      type: 'bar',
      data: {
        labels: this.labels,
        nb_obs_per_category: this.nb_obs_per_category,
        datasets,
      },
      options: {
        responsive: false,

        legend: {
          display: false,
        },

        tooltips: {
          position: 'nearest',
          enabled: false,
          custom: function(ttm) {
            return parameterizeTooltip.bind(this)(0, ttm)
          },
          callbacks: {
            label: format_generic_label,
          },
        },

        scales: {
          xAxes: [
            {
              display: false,
            },
          ],
          yAxes: [
            {
              display: false,
              ticks: {
                beginAtZero: true,
                max: this.max_value,
              },
            },
          ],
        },
      },
    })
  }
}

// *****************************************************************************

export default {
  name: 'ColumnstoreColumnHistogram',
  components: {},
  props: ['histogram', 'nb_obs', 'empty_percentage', 'cell_width'],
  data() {
    return {
      chart_object: undefined,
    }
  },
  computed: {},
  mounted() {
    this.build_charts()
  },
  beforeDestroy() {
    this.destroy_charts()
    cleanup_parameterizeTooltip()
  },
  watch: {
    histogram() {
      this.build_charts()
    },
    nb_obs() {
      this.build_charts()
    },
    empty_percentage() {
      this.build_charts()
    },
    cell_width() {
      this.build_charts()
    },
  },
  methods: {
    destroy_charts() {
      if (this.chart_object) {
        this.chart_object.real_chart.destroy()
        this.chart_object = undefined
      }
      if (this.empty_chart) {
        this.empty_chart.real_chart.destroy()
        this.empty_chart = undefined
      }
    },

    build_charts() {
      this.destroy_charts()

      const max_nb_bars = this.histogram.histogram.length
      const max_bar_width = Math.floor(this.cell_width / max_nb_bars)
      const bar_width = _.clamp(max_bar_width, 5 + 1, 23 + 1)
      const nb_bars = Math.min(
        Math.floor(this.cell_width / bar_width),
        max_nb_bars
      )
      this.$refs['hits-chart'].width = bar_width * nb_bars
      this.chart_object = new GenericChart(
        this.$refs['hits-chart'],
        this.histogram,
        bar_width,
        nb_bars,
        this.nb_obs
      )

      if (!_.isUndefined(this.empty_percentage)) {
        this.empty_chart = new EmptyPercentageChart(
          this.$refs['empty-percentage-chart'],
          this.empty_percentage
        )
      }
    },
  },
}
</script>
